"use strict";
/**
 * GA redirect interstitial — destination validation and the Builder-managed
 * destination whitelist.
 *
 * This is NOT part of the `url-redirects` pipeline in middlewareUtils.ts, and
 * nothing here is reachable from middleware. The distinction matters:
 *
 * - `url-redirects` matches inbound paths on domains we own and is deliberately
 *   permissive about where it sends traffic, because plenty of vanity URLs
 *   legitimately point at non-Orlando Health destinations.
 * - This module guards a single route (`/ga-redirect`) whose destination is
 *   supplied by the *caller* in a query param. That is an open-redirect surface
 *   (CWE-601), so it must be restrictive: only hosts on the whitelist.
 *
 * The route exists so QR-code traffic lands on a real page long enough for GTM
 * to fire a GA beacon before bouncing onward. A middleware redirect returns
 * headers only — no HTML, no JS, no analytics.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_GA_REDIRECT_WHITELIST = exports.GA_REDIRECT_WHITELIST_ENTRY = exports.GA_REDIRECT_WHITELIST_MODEL = exports.GA_REDIRECT_EVENT = exports.GA_REDIRECT_DESTINATION_PARAM = exports.GA_REDIRECT_PATH = void 0;
exports.normalizeWhitelistEntry = normalizeWhitelistEntry;
exports.buildWhitelist = buildWhitelist;
exports.isHostWhitelisted = isHostWhitelisted;
exports.validateGaRedirectDestination = validateGaRedirectDestination;
exports.resetGaRedirectWhitelistCache = resetGaRedirectWhitelistCache;
exports.fetchGaRedirectWhitelist = fetchGaRedirectWhitelist;
/** Route that renders the interstitial. Kept here so the page, the eager-GTM
 * wiring in _app, and the reserved-source guard all agree on one value. */
exports.GA_REDIRECT_PATH = "/ga-redirect";
/** Query param carrying the final destination. */
exports.GA_REDIRECT_DESTINATION_PARAM = "redirect_url";
/** dataLayer event name the GTM tag triggers on. */
exports.GA_REDIRECT_EVENT = "ga_redirect";
exports.GA_REDIRECT_WHITELIST_MODEL = process.env.NEXT_PUBLIC_GA_REDIRECT_WHITELIST_MODEL || "ga-redirect-whitelist";
exports.GA_REDIRECT_WHITELIST_ENTRY = process.env.NEXT_PUBLIC_GA_REDIRECT_WHITELIST_ENTRY || "Google Analytics Redirects";
/**
 * Emergency floor, used ONLY when Builder cannot be reached and no previous
 * response is cached. Failing closed entirely would break every QR code on a
 * transient Builder outage; failing open to arbitrary hosts would hand out an
 * open redirect. This is the compromise — the intended destinations, hardcoded.
 */
exports.DEFAULT_GA_REDIRECT_WHITELIST = [
    "google.com",
    "youtube.com",
    "youtu.be",
    "facebook.com",
];
function stripTrailingDot(host) {
    return host.endsWith(".") ? host.slice(0, -1) : host;
}
/**
 * Turn one whitelist row into a bare registrable host.
 *
 * The Builder field is typed `url` and holds full URLs ("https://www.google.com/"),
 * but hand-typed bare domains ("youtube.com") are accepted too. The leading
 * "www." is stripped so a single row covers the apex and its subdomains, which
 * is how editors think about "allow Google".
 */
function normalizeWhitelistEntry(raw) {
    if (typeof raw !== "string")
        return null;
    const trimmed = raw.trim();
    if (!trimmed)
        return null;
    const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    let hostname;
    try {
        hostname = new URL(candidate).hostname;
    }
    catch {
        return null;
    }
    const host = stripTrailingDot(hostname.toLowerCase());
    if (!host)
        return null;
    return host.startsWith("www.") ? host.slice(4) : host;
}
/** Normalize + dedupe the raw `destinationWhitelist` rows from Builder. */
function buildWhitelist(rows) {
    if (!Array.isArray(rows))
        return [];
    const domains = new Set();
    for (const row of rows) {
        const url = row && typeof row === "object" ? row.url : row;
        const domain = normalizeWhitelistEntry(url);
        if (domain)
            domains.add(domain);
    }
    return [...domains];
}
/**
 * Suffix match, anchored on a dot.
 *
 * Deliberately NOT `host.includes(domain)`. middlewareUtils.ts uses `includes`
 * for `fromDomain`, which is fine there because it matches domains we own — but
 * reused here it would be a hole: "google.com.evil.com" contains "google.com".
 */
function isHostWhitelisted(hostname, whitelist) {
    const host = stripTrailingDot(hostname.toLowerCase());
    return whitelist.some((domain) => host === domain || host.endsWith(`.${domain}`));
}
/**
 * Validate a caller-supplied destination against the whitelist.
 *
 * On success returns the *parser's* normalized serialization (`URL.href`) — the
 * caller must redirect to that, never to the raw input. Re-serializing is what
 * neutralizes parser-differential tricks (stray backslashes, tabs, mixed
 * encoding): we go where our parser actually resolved, not where the input
 * looked like it pointed.
 */
function validateGaRedirectDestination(raw, whitelist) {
    if (typeof raw !== "string" || !raw.trim()) {
        return { ok: false, reason: "missing" };
    }
    const candidate = raw.trim();
    // WHATWG URL parsing silently strips tabs and newlines, so an embedded CR/LF
    // would otherwise sail through and land in a Location header. Reject interior
    // control characters and whitespace outright — no legitimate destination
    // contains them. Note this runs after trim(), so *surrounding* whitespace (a
    // trailing newline from a QR encoder, say) is tolerated rather than rejected.
    const hasUnsafeChar = [...candidate].some((ch) => {
        const code = ch.codePointAt(0) ?? 0;
        // C0 controls + space (0x00-0x20) and DEL (0x7F).
        return code <= 0x20 || code === 0x7f;
    });
    if (hasUnsafeChar) {
        return { ok: false, reason: "malformed" };
    }
    let parsed;
    try {
        // No base URL, on purpose: that rejects protocol-relative ("//evil.com")
        // and bare paths, which would otherwise resolve against our own origin.
        parsed = new URL(candidate);
    }
    catch {
        return { ok: false, reason: "not-absolute" };
    }
    // Blocks javascript:, data:, and plain-http downgrades in one check.
    if (parsed.protocol !== "https:") {
        return { ok: false, reason: "unsupported-scheme" };
    }
    // "https://google.com@evil.com/" parses with hostname "evil.com"; the host
    // check below already catches it, but credentials in a marketing redirect are
    // never legitimate and the explicit reason makes the log line obvious.
    if (parsed.username || parsed.password) {
        return { ok: false, reason: "embedded-credentials" };
    }
    if (!parsed.hostname) {
        return { ok: false, reason: "malformed" };
    }
    // `parsed.hostname` is punycode for internationalized hosts, so comparing it
    // against an ASCII whitelist is homograph-safe for free.
    if (!isHostWhitelisted(parsed.hostname, whitelist)) {
        return { ok: false, reason: "not-whitelisted" };
    }
    return { ok: true, href: parsed.href, hostname: parsed.hostname };
}
// Cached per server instance so a QR scan does not pay for a Builder round trip.
// A TTL (rather than build-time baking) is the point: an editor adding a domain
// in Builder must take effect without redeploying the app.
let whitelistCache = null;
/** Test hook — drops the module-level cache. */
function resetGaRedirectWhitelistCache() {
    whitelistCache = null;
}
async function requestWhitelistRows(fetchImpl, model, apiKey, entryName) {
    const base = `https://cdn.builder.io/api/v3/content/${encodeURIComponent(model)}`;
    const common = `apiKey=${encodeURIComponent(apiKey)}&limit=1&noTargeting=true&fields=data`;
    const byName = await fetchImpl(`${base}?${common}&query.name=${encodeURIComponent(entryName)}`);
    if (byName.ok) {
        const json = (await byName.json());
        const rows = json.results?.[0]?.data?.destinationWhitelist;
        if (rows)
            return rows;
    }
    // The entry was renamed in Builder, or the env override is stale. The model
    // holds a single entry by design, so fall back to whatever is there rather
    // than dropping to the hardcoded floor.
    const anyEntry = await fetchImpl(`${base}?${common}`);
    if (!anyEntry.ok) {
        throw new Error(`Builder responded ${anyEntry.status}`);
    }
    const json = (await anyEntry.json());
    return json.results?.[0]?.data?.destinationWhitelist;
}
/**
 * Read the whitelist, preferring cache, then Builder, then the last-good cached
 * value, then the hardcoded floor. Never returns an empty list — an empty
 * whitelist would reject every destination and look like a total outage.
 */
async function fetchGaRedirectWhitelist(options) {
    const { apiKey, model = exports.GA_REDIRECT_WHITELIST_MODEL, entryName = exports.GA_REDIRECT_WHITELIST_ENTRY, ttlSeconds = Number(process.env.GA_REDIRECT_WHITELIST_TTL_SECONDS || 300), fetchImpl = fetch, } = options;
    const now = Date.now();
    if (whitelistCache && whitelistCache.expiresAt > now) {
        return whitelistCache.domains;
    }
    if (apiKey) {
        try {
            const rows = await requestWhitelistRows(fetchImpl, model, apiKey, entryName);
            const domains = buildWhitelist(rows);
            if (domains.length) {
                whitelistCache = { domains, expiresAt: now + ttlSeconds * 1000 };
                return domains;
            }
            console.warn(`[ga-redirect] whitelist entry "${entryName}" in model "${model}" produced no usable domains`);
        }
        catch (error) {
            console.error(`[ga-redirect] failed to load whitelist from Builder: ${error instanceof Error ? error.message : String(error)}`);
        }
    }
    else {
        console.error("[ga-redirect] no Builder API key available to load the whitelist");
    }
    if (whitelistCache) {
        console.warn("[ga-redirect] serving stale cached whitelist");
        return whitelistCache.domains;
    }
    console.warn("[ga-redirect] falling back to DEFAULT_GA_REDIRECT_WHITELIST");
    return exports.DEFAULT_GA_REDIRECT_WHITELIST;
}
